import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { formatDate, formatDayTime, type OrderListResponse, RACK_TYPE_SHORT } from '@reikraft/shared';
import { Empty, ErrorBox, Icon, Loader, Panel, StatusBadge } from '@reikraft/ui';
import { api } from '../api';
import { Hint, PageHead } from '../components/common';
import { useTz } from '../session';

/** Приёмка: новые заявки, забор реек и поиск по бирке */
export function IntakePage() {
  const navigate = useNavigate();
  const tz = useTz();
  const [code, setCode] = useState('');
  const list = useQuery({
    queryKey: ['orders', 'requests', '', '1'],
    queryFn: () => api.get<OrderListResponse>('/admin/orders?tab=requests&size=100'),
  });
  const items = list.data?.items ?? [];
  const fresh = items.filter((o) => o.status === 'new');
  const pickups = items.filter((o) => o.status === 'pickup');

  const scan = (e: FormEvent) => {
    e.preventDefault();
    const c = code.trim().replace(/\D/g, '').padStart(6, '0');
    if (c.replace(/0/g, '')) void navigate(`/u/${c}`);
  };

  return (
    <>
      <PageHead
        title="Приёмка"
        sub="Новые заявки, выезды за рейками и поиск рейки по бирке"
        actions={
          <Link to="/orders/new" className="rk-btn rk-btn--primary">
            <Icon name="plus" size={16} /> Принять рейку без заявки
          </Link>
        }
      />
      <form onSubmit={scan} className="rk-row" style={{ ['--rk-gap' as string]: '8px', maxWidth: 520 }}>
        <label className="rk-search rk-grow">
          <Icon name="qr" size={16} style={{ color: 'var(--muted)' }} />
          <span className="rk-sr">Код бирки</span>
          <input inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Код бирки, например 000187" />
        </label>
        <button type="submit" className="rk-btn">
          Найти рейку
        </button>
      </form>
      <Hint>QR-код на бирке открывает карточку рейки сразу — достаточно навести камеру телефона.</Hint>

      {list.isLoading ? (
        <Loader />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => list.refetch()} />
      ) : (
        <div className="adm-cols">
          <div className="adm-cols__main">
            <Panel title={`Новые заявки · ${fresh.length}`} bodyless>
              {fresh.length ? (
                <div className="adm-cards" style={{ border: 0 }}>
                  {fresh.map((o) => (
                    <Link key={o.id} to={`/orders/${o.id}`} className="adm-card">
                      <span className="adm-card__top">
                        <b className="rk-mono">{o.number}</b>
                        <StatusBadge status={o.status} kind={o.kind} />
                      </span>
                      <span>
                        <b>{o.party?.name ?? '—'}</b> · {o.car ?? 'авто не указано'}
                        {o.rackType ? <span className="rk-muted rk-mono"> · {RACK_TYPE_SHORT[o.rackType]}</span> : null}
                      </span>
                      <span className="adm-card__sub">
                        <span>{o.kind === 'sale' ? 'Покупка со склада' : o.kind === 'exchange' ? 'Обмен' : 'Ремонт'}</span>
                        <span className="rk-mono">{formatDayTime(o.createdAt, tz)}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <Empty>Новых заявок нет</Empty>
              )}
            </Panel>
          </div>
          <div className="adm-cols__side">
            <Panel title={`Забрать · ${pickups.length}`} bodyless>
              {pickups.length ? (
                <div className="adm-cards" style={{ border: 0 }}>
                  {pickups.map((o) => (
                    <Link key={o.id} to={`/orders/${o.id}`} className="adm-card">
                      <span className="adm-card__top">
                        <b>{o.party?.name ?? '—'}</b>
                        <span className="rk-mono">{o.number}</span>
                      </span>
                      <span className="adm-card__sub">
                        <span>
                          {o.car ?? '—'}
                          {o.plate ? ` · ${o.plate}` : ''}
                        </span>
                        <span className="rk-mono">{o.dueDate ? formatDate(o.dueDate) : ''}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <Empty>Выездов нет</Empty>
              )}
            </Panel>
          </div>
        </div>
      )}
    </>
  );
}
